import React from "react";
import { default as MUIButton } from "@mui/material/Button";
import styled from "styled-components";

/**
 * Button component for user interactions.
 *
 * @component
 * @example
 * const handleClick = () => {
 *   console.log("Button clicked!");
 * };
 *
 * <Button
 *   onClick={handleClick}
 *   color="primary"
 *   variant="contained"
 *   label="Click me"
 *   disabled={false}
 *   startIcon={<Icon />}
 *   endIcon={<Icon />}
 *   fullWidth={false}
 *   type="button"
 *   href="#"
 *   style={{ margin: '10px' }}
 *   toolTipTitle="Tooltip text"
 *   id="uniqueButtonId"
 * />
 *
 * @param {Function} props.onClick - Function to be called when the button is clicked.
 * @param {string} [props.color="primary"] - The color of the button. Possible values: "primary", "secondary", etc.
 * @param {string} [props.variant="contained"] - The variant of the button. Possible values: "contained", "outlined", etc.
 * @param {string} props.label - The label text of the button.
 * @param {boolean} [props.disabled] - If true, the button will be disabled.
 * @param {ReactNode} [props.startIcon] - The icon to be displayed before the label.
 * @param {ReactNode} [props.endIcon] - The icon to be displayed after the label.
 * @param {boolean} [props.fullWidth] - If true, the button will take the full width of its container.
 * @param {string} [props.type] - The type attribute of the button. e.g., "button", "submit", "reset".
 * @param {string} [props.href] - If provided, the button will act as a link with the specified href.
 * @param {Object} [props.style] - Additional inline styles for the button.
 * @param {string} [props.toolTipTitle] - The title text for the tooltip.
 * @param {string} [props.id] - The unique identifier for the button.
 * @param {string} [props.tabindex] - Tabindex value.
 * @param {boolean} [props.ariaHidden] - button aria-hidden attrbute.
 * @param {ReactNode} [props.children] - DEPRECATED. The children to render the label.

 *
 * @returns {JSX.Element} Returns the Button component.
 */

const StyledMUIButton = styled(MUIButton)`
  border-radius: 8px !important;
  text-transform: capitalize !important;
  height: 42px;
  text-transform: none !important;
  box-shadow: none !important;
  font-family: var(--primaryTextFont) !important;

  &[class*="MuiButton-containedPrimary"]:hover {
    background-color: var(--primaryDarkColor) !important;
    color: var(--primaryDarkContrastColor) !important;
  }
`;

export const Button = ({
  onClick,
  color = "primary",
  variant = "contained",
  label,
  disabled,
  startIcon,
  endIcon,
  fullWidth,
  type,
  href,
  style,
  toolTipTitle,
  id,
  tabindex,
  ariaHidden,
  className,
  children,
  size,
  sx,
}) => {
  return (
    <StyledMUIButton
      onClick={onClick}
      color={color}
      variant={variant}
      disabled={disabled}
      startIcon={startIcon}
      endIcon={endIcon}
      fullWidth={fullWidth}
      type={type}
      href={href}
      style={style}
      title={toolTipTitle}
      id={id}
      size={size}
      sx={sx}
      {...(tabindex != null ? { tabIndex: tabindex } : {})}
      {...(ariaHidden != null ? { "aria-hidden": ariaHidden } : {})}
      className={className}
    >
      {label || children}
    </StyledMUIButton>
  );
};
